import {Inject, Injectable, DOCUMENT} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {User} from '../../interfaces/user';
import {Router} from '@angular/router';
import {LangSrcService} from '../lang-src/lang-src.service';
import {UserService} from '../user/user.service';
import moment from 'moment-timezone';
import {ToolsService} from '../tools/tools.service';
import {Subject} from 'rxjs';
import {StorageMap} from '@ngx-pwa/local-storage';

import jwt_decode from 'jwt-decode';
import {WefitSsoService} from '../sso/wefit-sso.service';
import {AccessService} from '../access/access.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  signInEvent = new Subject<void>();

  constructor(
    private storage: StorageMap,
    private http: HttpClient,
    private toolsSrv: ToolsService,
    private router: Router,
    private langSrcSrv: LangSrcService,
    private userSrv: UserService,
    private wefitSsoSrv: WefitSsoService,
    private accessSrv: AccessService,
    @Inject(DOCUMENT) private doc: Document
  ) {
  }

  /**
   * Store token string
   * @param token string
   */
  async storeToken(token: string) {
    await this.storage.set('token', token).toPromise();
    await this.setSessionLifetime();
  }

  /**
   * Get user and store object
   */
  async storeUser() {
    const user: any = await this.userSrv.me();
    await this.storage.set('user', user).toPromise();
    this.signInEvent.next();
  }

  /**
   * Return auth status
   * @return boolean
   */
  async isLogged(): Promise<boolean> {
    const token = await this.storage.get('token').toPromise();
    if (!token || this.tokenExpired(token)) {
      return false;
    }
    return true;
  }

  async isReallyLogged() {
    const isLogged = await this.isLogged();
    if (!isLogged) {
      return false;
    }
    const lifetime: any = await this.storage.get('user-session-lifetime').toPromise();
    if (!lifetime) {
      return false;
    }
    if ((!lifetime || !lifetime.expireAt || lifetime.expireAt <= moment().valueOf())) {
      return false;
    }
    return true;
  }

  /**
   * Return stored user object
   * @return User
   */
  async getUser(): Promise<any> {
    return await this.storage.get('user').toPromise();
  }

  async setSessionLifetime() {
    await this.storage.set('user-session-lifetime', {
      expireAt: moment().add(
        environment.auth.sessionLifetime,
        'days'
      ).valueOf()
    }).toPromise();
  }

  register(params: any) {
    return this.http.post(`${this.toolsSrv.getApiEndpoint()}auth/signup`, params);
  }

  login(params: any) {
    return this.http.post(`${this.toolsSrv.getApiEndpoint()}auth/signin`, {
      mail: params.login,
      password: params.password
    });
  }

  recovery(params: any) {
    return this.http.post(`${this.toolsSrv.getApiEndpoint()}auth/recovery`, params);
  }

  recoveryConfirm(params: any) {
    return this.http.post(`${this.toolsSrv.getApiEndpoint()}auth/recovery-confirm`, params);
  }

  async logout(redirect: boolean = true) {
    await this.storage.delete('user').toPromise();
    await this.storage.delete('token').toPromise();
    await this.storage.delete('user-session-lifetime').toPromise();
    await this.storage.delete('platform').toPromise();
    await this.wefitSsoSrv.clearToken();
    this.accessSrv.reset();
    this.signInEvent.next();
    if (redirect) {
      this.doc.location.href = 'https://lms.wefitgroup.com/connect_out.cfm';
    }
  }

  public tokenExpired(token: any): boolean {
    try {
      const decodedToken: any = jwt_decode(token);
      const currentTime = Math.floor(Date.now() / 1000);

      return decodedToken.exp < currentTime;
    } catch (error) {
      console.error(` GS :  Error decoding token : ${error?.message}`);
      return true;
    }
  }
}
